<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import Slide from '@/components/WelcomeSeries/Slide.vue'
import Foot from '@/components/WelcomeSeries/Foot.vue'
import { userModule } from '@/store/user/module'
import { bundleModule } from '@/store/bundle/module'
import { examMetadataModule } from '@/store/examMetadata/module'

const ModalContainer = UIKit.ModalContainer
const Modal = UIKit.Modal
const PocketButton = UIKit.Button

const emit = defineEmits<{
    close: []
}>()

const currentSlideIndex = ref(0)
const slides = [
    {
        title: 'Study Anywhere, Anytime',
        description: 'Take your Pocket Prep content with you on any device using our app or here on web.',
        imageName: 'welcome images 01.png',
    },
    {
        title: 'Our explanations are here to help!',
        description: 'Thorough explanations help you learn along the way.',
        imageName: 'welcome images 02.png',
    },
    {
        title: 'Subject Insights',
        description: 'Track performance by subject to know where you need to focus.',
        imageName: 'welcome images 03.png',
    },
    {
        title: 'Our Pass Guarantee*',
        description: 'If you fail your exam, we\'ll give you three months free as long as you\'ve been subscribed '
            + 'for three months, attempted all practice questions, and maintained an 80% average.',
        imageName: 'welcome images 04.png',
        imageAlt: 'Pass Guarantee, Only with premium',
    },
]

const currentSlide = computed(() => {
    return slides[currentSlideIndex.value] || slides[0]
})

onMounted(async () => {
    await Promise.all([
        bundleModule.actions.fetchBundles(),
        examMetadataModule.actions.fetchExamMetadata(),
    ])
    nextTick(() => {
        const wsmTitleEl: HTMLElement | null = document.querySelector('.wsm__title')
        wsmTitleEl?.focus()
    })
})

const closeModal = () => {
    userModule.actions.updateWebConfig({
        hasSeenWelcomeModal: true,
    })

    emit('close')
}

const changeSlide = (newSlideIndex: number) => {
    currentSlideIndex.value = newSlideIndex
}
</script>

<template>
    <Portal to="modal">
        <ModalContainer key="welcomeSeriesModal" @close="closeModal">
            <template #modal>
                <Modal
                    class="wsm__modal"
                    :show-close-button="true"
                    @close="closeModal"
                >
                    <div class="wsm">
                        <div class="wsm__top">
                            <img src="@/assets/welcomeSeries/celebrate-icon.png" alt="">
                            <div
                                class="wsm__title"
                                tabindex="-1"
                            >
                                Welcome to Pocket Prep!
                            </div>
                            <div class="wsm__subtitle">
                                Your journey to study greatness begins! <br> Here are some basics to get you started:
                            </div>
                        </div>
                        <div class="wsm__middle">
                            <Slide
                                v-if="currentSlide"
                                :title="currentSlide.title"
                                :description="currentSlide.description"
                                :image-name="currentSlide.imageName"
                                :image-alt="currentSlide.imageAlt || ''"
                            />
                        </div>
                        <Foot
                            :current-dot="currentSlideIndex"
                            @close="closeModal"
                            @change="changeSlide"
                        />
                        <div v-if="currentSlideIndex >= 3" class="wsm__close">
                            <PocketButton class="wsm__close-button" @click="closeModal">
                                Got It
                            </PocketButton>
                        </div>
                    </div>
                </Modal>
            </template>
        </ModalContainer>
    </Portal>
</template>

<style lang="scss" scoped>
.wsm {
    width: 100%;
    height: 100%;
    position: relative;
    border-radius: 12px;
    overflow: hidden;

    &__modal {
        width: 1010px;
        height: 80vh;
        min-height: 640px;
        left: 50%;
        position: absolute;
        transform: translateX(-50%);
        top: 80px;
        background-color: $floral-white;

        @include breakpoint(brown-bear) {
            width: 740px;
            height: calc(100vh - 140px) !important;
        }

        @include breakpoint(black-bear) {
            top: 0;
            max-height: none;
            height: 100% !important;
            width: 100%;
            min-height: 660px;
            border-radius: 0;
        }
    }

    &__top {
        padding-top: 78px;
        text-align: center;
        width: 334px;
        margin: 0 auto;
        position: relative;
        z-index: 1;

        @include breakpoint(black-bear) {
            padding-top: 38px;
            width: 100%;
        }

        img {
            display: block;
            height: 26px;
            margin: 0 auto 12px;

            @include breakpoint(black-bear) {
                margin: 0 auto 8px;
            }
        }
    }

    &__title {
        font-size: 26px;
        line-height: 36px;
        font-weight: 700;
        margin-bottom: 12px;
        outline: none;

        @include breakpoint(black-bear) {
            font-size: 26px;
            line-height: 36px;
            width: 228px;
            margin: 0 auto 9px;
        }
    }

    &__subtitle {
        color: $slate-03;
        font-size: 15px;
        line-height: 19px;

        @include breakpoint(black-bear) {
            max-width: 302px;
            margin: 0 auto;
        }
    }

    &__middle {
        height: 321px;
        margin-bottom: 8px;

        @include breakpoint(black-bear) {
            height: 408px;
            margin-bottom: 0;
        }
    }

    &__close {
        position: absolute;
        right: 30px;
        bottom: 30px;

        @include breakpoint(black-bear) {
            width: 174px;
            bottom: 110px;
            left: 50%;
            right: auto;
            margin-left: -87px;
        }
    }

    &__close-button {
        @include breakpoint(black-bear) {
            width: 100%;
            height: 44px;
        }
    }
}
</style>
